import { motion } from "framer-motion";
import { ScrollMaskedLines } from "./MaskedText";
import ShareBar from "./ShareBar";

/**
 * Rebecca's direct testimony — verbatim mission statement from Danielle's mother.
 * Placed between the film and the chapters as the emotional throughline of the site.
 */
export default function MothersVoice() {
    return (
        <section
            data-testid="mothers-voice-section"
            id="mission"
            className="relative w-full bg-neutral-950 text-neutral-50 py-32 sm:py-48 overflow-hidden border-y border-neutral-800"
        >
            {/* Soft candle backdrop */}
            <div className="absolute inset-0 opacity-60 pointer-events-none">
                <img
                    src="/scenes/candle_flame.png"
                    alt=""
                    aria-hidden="true"
                    className="editorial-img w-full h-full object-cover"
                />
            </div>
            {/* Vignette + readability scrim — kept lighter so the flame reads through */}
            <div className="absolute inset-0 bg-gradient-to-b from-neutral-950/70 via-neutral-950/30 to-neutral-950/80 pointer-events-none" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,transparent_35%,rgba(10,10,10,0.85)_100%)] pointer-events-none" />

            <div className="relative max-w-[92rem] mx-auto px-6 sm:px-12 lg:px-16 grid grid-cols-12 gap-6">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-15%" }}
                    transition={{ duration: 1 }}
                    className="col-span-12 sm:col-span-3 font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-400"
                >
                    A mother&apos;s testimony
                </motion.div>

                <div className="col-span-12 sm:col-span-9">
                    {/* Opening — Mother-and-daughter photo + story */}
                    <div className="grid grid-cols-12 gap-6 sm:gap-10 items-start mb-2">
                        {/* Photo — first on mobile, left on desktop */}
                        <motion.figure
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-15%" }}
                            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                            data-testid="testimony-photo"
                            className="col-span-12 md:col-span-5 order-1"
                        >
                            <div className="relative w-full aspect-[16/11] overflow-hidden bg-neutral-900 border border-neutral-800">
                                <img
                                    src="/danielle/mommy_and_danielle.jpg"
                                    alt="Danielle and her mother Rebecca — outdoors, both smiling."
                                    loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover object-center"
                                />
                                {/* Subtle edge vignette so the photo settles into the dark section */}
                                <div
                                    aria-hidden="true"
                                    className="absolute inset-0 pointer-events-none"
                                    style={{
                                        boxShadow:
                                            "inset 0 0 60px 12px rgba(0,0,0,0.55)",
                                    }}
                                />
                            </div>
                            <figcaption className="mt-3 font-sans text-[10px] uppercase tracking-[0.3em] text-neutral-400">
                                Danielle and her mother, Rebecca · The promise began here.
                            </figcaption>
                        </motion.figure>

                        {/* Text — after photo on mobile, right on desktop */}
                        <div className="col-span-12 md:col-span-7 order-2">
                            {/* Opening quote mark */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true, margin: "-15%" }}
                                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                                className="font-serif italic text-7xl sm:text-8xl leading-none text-neutral-500 mb-4 sm:mb-6 select-none"
                                aria-hidden="true"
                            >
                                &ldquo;
                            </motion.div>

                            {/* Opening — Her name was Danielle */}
                            <motion.div
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-15%" }}
                                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                                className="font-serif text-2xl sm:text-3xl leading-[1.25] tracking-tight text-neutral-50 space-y-6"
                            >
                                <p className="font-semibold text-white">
                                    She was my only child. I stood before the
                                    court myself and was appointed her legal
                                    guardian. That was a promise, and I kept
                                    it every day of her life.
                                </p>
                                <p>
                                    Danielle was born on August 5, 1993. Her
                                    birth was delayed at Long Island Jewish
                                    Hospital — leaving her with special needs
                                    for the rest of her life. But you would
                                    never know it from the way she loved the
                                    world.
                                </p>
                            </motion.div>
                        </div>
                    </div>

                    {/* Continued opening story — full width */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 1.2, delay: 0.15 }}
                        className="mt-8 sm:mt-10 max-w-4xl font-serif text-2xl sm:text-3xl lg:text-4xl leading-[1.25] tracking-tight text-neutral-50 space-y-6"
                    >
                        <p>
                            Two to three days a week she went to the nursing
                            home — serving breakfast and lunch to the elderly,
                            sitting with them, holding their hands. Children
                            were drawn to her everywhere she went. She bowled.
                            She laughed. She was healthy, strong, and full of
                            life.
                        </p>
                        <p>
                            She had never spent a single night without her
                            family.
                        </p>
                        <p>
                            On August 27, 2021, I brought her to Northwell
                            Health Glen Cove Hospital with a cough. Her oxygen
                            was 96%. Her blood work was normal. I held her hand
                            walking through those doors and kissed her forehead.
                        </p>
                        <p className="font-semibold text-white">
                            That was the last time she held my hand. The last
                            time I saw her eyes open. The last time she called
                            me Mommy.
                        </p>
                    </motion.div>

                    {/* Big pull-quote — the warning */}
                    <motion.blockquote
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-16 sm:mt-24 font-serif text-4xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.02] tracking-tight text-neutral-50 max-w-5xl"
                    >
                        <span className="block">This is not just</span>
                        <span className="block">Danielle&rsquo;s story.</span>
                        <span className="block text-white">This is a warning.</span>
                    </motion.blockquote>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 1.2, delay: 0.4 }}
                        className="mt-14 sm:mt-20 max-w-3xl space-y-8 font-serif text-xl sm:text-2xl lg:text-[26px] leading-snug text-neutral-200"
                    >
                        <p>
                            Northwell Health locked me out on September 1st.
                            Instead of someone to comfort her, they tied her to
                            the bed — a frightened child, crying for her Mommy,
                            alone among strangers. They did not call me. They
                            chose restraints over compassion. Protocol over
                            humanity.
                        </p>
                        <p>
                            Danielle spent thirty-two days on a ventilator,
                            sedated, unable to speak for herself. I was locked
                            out of the building for thirteen of those days.
                        </p>
                        <p>
                            There are serious discrepancies in Danielle&rsquo;s
                            certified medical records.{" "}
                            <span className="italic text-neutral-50">
                                They are documented, they are before the court,
                                and they will be addressed there.
                            </span>
                        </p>
                        <p className="text-neutral-50">
                            On October 6, 2021, Danielle died. She was alone.{" "}
                            <span className="italic">
                                I never got to say goodbye.
                            </span>
                        </p>
                        <p>
                            Rebecca prepared and filed the case herself, pro se,
                            before retaining counsel.{" "}
                            <span className="italic text-neutral-50">
                                No attorney. No legal training. No money.
                            </span>{" "}
                            Just a mother who refused to let her daughter&rsquo;s
                            truth die with a deadline.
                        </p>
                        <p className="font-serif text-2xl sm:text-3xl lg:text-4xl text-neutral-50">
                            I refuse to be silent.{" "}
                            <span className="italic text-neutral-300">
                                And I will not let them get away with it.
                            </span>
                        </p>
                        <p>
                            I need your help to expose and support my case. Our
                            future depends on safe and trusted hospitals, doctors,
                            and nurses. It all starts with your willingness to
                            say —{" "}
                            <span className="italic text-neutral-50">
                                yes, I will stand with you, for the fate of our
                                children and grandchildren.
                            </span>
                        </p>
                    </motion.div>

                    {/* Attribution */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 1, delay: 1.1 }}
                        className="mt-14 sm:mt-20 flex items-center gap-6"
                    >
                        <span className="w-16 h-px bg-neutral-500" />
                        <div>
                            <div className="font-serif text-2xl sm:text-3xl text-neutral-50 flex items-center gap-3">
                                Rebecca Charles Jackson
                                <span className="text-amber-400 text-xl" aria-hidden="true">♥</span>
                            </div>
                            <div className="mt-1 font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                                Mother · Legal Guardian · Her Voice
                            </div>
                        </div>
                    </motion.div>

                    {/* Share row */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 1, delay: 1.3 }}
                        className="mt-14 sm:mt-20 flex flex-wrap items-center gap-6 sm:gap-10 pt-8 border-t border-neutral-800 font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-400"
                    >
                        <a
                            href="#candle"
                            data-testid="voice-cta-candle"
                            className="ink-link text-neutral-50 hover:text-neutral-200"
                        >
                            Light a candle →
                        </a>
                        <a
                            href="#follow"
                            data-testid="voice-cta-follow"
                            className="ink-link text-neutral-50 hover:text-neutral-200"
                        >
                            Follow the case →
                        </a>
                        <a
                            href="https://rebeccacharles.substack.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="voice-cta-substack"
                            className="ink-link text-neutral-50 hover:text-neutral-200"
                        >
                            Read Rebecca&apos;s Substack →
                        </a>
                        <span className="tabular text-neutral-500">
                            #JusticeForDanielle
                        </span>
                    </motion.div>

                    {/* Share on social */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 1, delay: 1.4 }}
                        className="mt-10 sm:mt-12"
                    >
                        <ShareBar
                            variant="dark"
                            label="Share her story"
                            text="Danielle walked into a hospital with a cough. Forty-one days later she was gone. Read her story. Stand with her family. #JusticeForDanielle #DanielleAlvarez"
                        />
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
